import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Accordion,
  ListGroup,
  Badge,
  Pagination,
  InputGroup,
  Modal,
  Table,
} from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL;

const getLevelNameFromId = (id) => {
  switch (id) {
    case 7:
      return "G";
    case 1:
      return "F";
    case 2:
      return "E";
    case 3:
      return "D";
    case 4:
      return "C";
    case 5:
      return "B";
    case 6:
      return "A";
    default:
      return `ID ${id}`;
  }
};

const TournamentManagement = () => {
  const [prompt, setPrompt] = useState("");
  const [tournaments, setTournaments] = useState([]);
  const [players, setPlayers] = useState([]); // State to hold players list
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ type: "", msg: "" });
  const [needsRefresh, setNeedsRefresh] = useState(false);

  // State for the Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  const [editingTournamentContent, setEditingTournamentContent] = useState("");
  const [schedules, setSchedules] = useState({}); // State to hold schedules, keyed by tournament ID


  // States for search and pagination, keyed by tournament ID
  const [searchTerms, setSearchTerms] = useState({});
  const [currentPages, setCurrentPages] = useState({});
  const itemsPerPage = 5; // Show 5 registrations per page in the admin view

  // Create a map for quick player lookup
  const playerMap = new Map(players.map((p) => [p.id, p.name]));

  const fetchData = useCallback(async () => {
    try {
      const [tourneyRes, playersRes] = await Promise.all([
        axios.get(`${API_URL}/tournaments/all-json`),
        axios.get(`${API_URL}/players?limit=all`),
      ]);
      setTournaments(tourneyRes.data);
      setPlayers(playersRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
      setNotification({
        type: "danger",
        msg: "Không thể tải dữ liệu cần thiết.",
      });
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, needsRefresh]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotification({ type: "", msg: "" });
    try {
      const res = await axios.post(`${API_URL}/tournaments/generate-with-ai`, {
        prompt,
      });
      setNotification({
        type: "success",
        msg: `Tạo giải đấu thành công! Link đăng ký: /register-tournament/${res.data.tournamentId}`,
      });
      setPrompt("");
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi tạo giải đấu.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRegistration = async (tournamentId, player1Id) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa đăng ký này?")) {
      try {
        await axios.delete(`${API_URL}/tournaments/${tournamentId}/register`, {
          data: { player_1_id: player1Id },
        });
        setNotification({
          type: "success",
          msg: "Đã xóa đăng ký thành công.",
        });
        setNeedsRefresh((p) => !p);
      } catch (err) {
        setNotification({
          type: "danger",
          msg: err.response?.data || "Lỗi khi xóa đăng ký.",
        });
      }
    }
  };

  const handleGenerateDraw = async (tournamentId) => {
    if (
      !window.confirm(
        `Bạn có chắc chắn muốn chia bảng và tạo lịch thi đấu cho giải đấu này không? Một giải đấu mới sẽ được tạo tự động dựa trên tên file. Thao tác này không thể hoàn tác.`,
      )
    ) {
      return;
    }

    setLoading(true);
    setNotification({ type: "", msg: "" });
    try {
      await axios.post(`${API_URL}/tournaments/${tournamentId}/generate-draw`);
      setNotification({
        type: "success",
        msg: "Chia bảng và tạo lịch thi đấu thành công!",
      });
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi thực hiện.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleShowEditModal = async (tournamentId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/tournaments/${tournamentId}/details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditingTournament(res.data);
      setEditingTournamentContent(JSON.stringify(res.data, null, 2));
      setShowEditModal(true);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi tải dữ liệu giải đấu.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingTournament(null);
    setEditingTournamentContent("");
  };

  const handleSaveEdit = async () => {
    if (!editingTournament) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const updatedTournamentData = JSON.parse(editingTournamentContent);
      await axios.put(`${API_URL}/tournaments/${editingTournament.id}`, updatedTournamentData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      handleCloseEditModal();
      setNotification({ type: "success", msg: "Đã cập nhật giải đấu thành công." });
      setNeedsRefresh(p => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi lưu giải đấu.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTournament = async (tournamentId) => {
    if (
      !window.confirm(
        "Bạn có chắc chắn muốn xóa TOÀN BỘ giải đấu này không? Hành động này không thể hoàn tác.",
      )
    ) {
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/tournaments/${tournamentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotification({
        type: "success",
        msg: "Đã xóa giải đấu thành công.",
      });
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi xóa giải đấu.",
      });
    }
  };

  const handleFetchSchedule = async (tournamentId) => {
    // Avoid refetching if already loaded
    if (schedules[tournamentId]) {
      // Toggle visibility by setting it to null/undefined
      setSchedules(prev => ({ ...prev, [tournamentId]: null }));
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/tournaments/${tournamentId}/schedule`);
      setSchedules(prev => ({ ...prev, [tournamentId]: res.data }));
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi tải lịch thi đấu.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleApproval = async (tournamentId, player1Id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/tournaments/${tournamentId}/register/approve`,
        { player_1_id: player1Id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setNotification({
        type: "success",
        msg: "Cập nhật trạng thái thành công.",
      });
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi cập nhật.",
      });
    }
  };

  const getRegistrationLink = (uuid) => {
    return `${window.location.origin}/register-tournament/${uuid}`;
  };

  return (
    <Container>
      <Row>
        <Col>
          <h2>Quản lý Giải đấu Cá nhân</h2>
        </Col>
      </Row>

      {notification.msg && (
        <Alert
          variant={notification.type}
          onClose={() => setNotification({ type: "", msg: "" })}
          dismissible
        >
          {notification.msg}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Tạo Yêu cầu Bốc thăm (AI)</Card.Title>
          <Form onSubmit={handleGenerate}>
            <Form.Group className="mb-3">
              <Form.Label>Nhập yêu cầu để tạo file đăng ký</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Ví dụ: Tạo giải đơn hạng A cho 32 vận động viên"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                required
              />
            </Form.Group>
            <Button type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Tạo Giải"}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <h3 className="mt-5">Danh sách Giải đấu</h3>
      <Accordion>
        {tournaments.map((tourney) => (
          <Accordion.Item key={tourney.id} eventKey={tourney.id}>
            <Accordion.Header>
              <span className="fw-bold me-3">{tourney.name}</span>
              <Badge bg="primary">
                {tourney.registrations.length} / {tourney.player_limit} VĐV
              </Badge>
            </Accordion.Header>
            <Accordion.Body>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <strong>ID:</strong> {tourney.id}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Hạng:</strong>{" "}
                  {Array.isArray(tourney.level_ids)
                    ? tourney.level_ids.map(getLevelNameFromId).join(", ")
                    : "N/A"}{" "}
                  | <strong>Loại:</strong> {tourney.type}
                </ListGroup.Item>
                {tourney.start_date && (
                  <ListGroup.Item>
                    <strong>Thời gian:</strong>{" "}
                    {new Date(tourney.start_date).toLocaleDateString("vi-VN")}
                  </ListGroup.Item>
                )}
                {tourney.location && (
                  <ListGroup.Item>
                    <strong>Địa điểm:</strong> {tourney.location}
                  </ListGroup.Item>
                )}
                <ListGroup.Item>
                  <strong>Link Đăng ký:</strong>{" "}
                  <a
                    href={getRegistrationLink(tourney.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {getRegistrationLink(tourney.id)}
                  </a>
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>VĐV đã đăng ký:</strong>
                  {(() => {
                    const searchTerm = searchTerms[tourney.id] || "";
                    const currentPage = currentPages[tourney.id] || 1;

					const filteredRegistrations = tourney.registrations.filter(
					  (reg) => {
						const p1Name =
						  playerMap.get(reg.player_1_id)?.toLowerCase() || "";
						const p2Name =
						  playerMap.get(reg.player_2_id)?.toLowerCase() || "";

						// Chuyển ID sang string để tìm kiếm
						const p1Id = reg.player_1_id.toString();
						const p2Id = reg.player_2_id ? reg.player_2_id.toString() : "";

						const search = searchTerm.toLowerCase();

						return (
						  p1Name.includes(search) ||
						  p2Name.includes(search) ||
						  p1Id.includes(searchTerm) ||
						  p2Id.includes(searchTerm)
						);
					  },
					);

                    const indexOfLastItem = currentPage * itemsPerPage;
                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                    const currentItems = filteredRegistrations.slice(
                      indexOfFirstItem,
                      indexOfLastItem,
                    );
                    const totalPages = Math.ceil(
                      filteredRegistrations.length / itemsPerPage,
                    );

                    if (tourney.registrations.length === 0) {
                      return " Chưa có VĐV nào.";
                    }

                    return (
                      <>
                        <InputGroup className="my-3">
                          <Form.Control
                            placeholder="Tìm VĐV trong giải này..."
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerms({
                                ...searchTerms,
                                [tourney.id]: e.target.value,
                              });
                              setCurrentPages({
                                ...currentPages,
                                [tourney.id]: 1,
                              });
                            }}
                          />
                        </InputGroup>
                        {currentItems.length > 0 ? (
                          <ListGroup>
                            {currentItems.map((reg, index) => (
                              <ListGroup.Item
                                key={index}
                                className="d-flex justify-content-between align-items-center"
                              >
                                <span>
                                  <strong>
                                    {indexOfFirstItem + index + 1}.{" "}
                                  </strong>
                            {playerMap.get(reg.player_1_id)
							  ? `${playerMap.get(reg.player_1_id)} (ID: ${reg.player_1_id})`
							  : `ID: ${reg.player_1_id}`}{" "}
							(Hạng: {getLevelNameFromId(reg.player_1_level_id)})
							{reg.player_2_id && (
							  <>
								{` & ${
								  playerMap.get(reg.player_2_id)
									? `${playerMap.get(reg.player_2_id)} (ID: ${reg.player_2_id})`
									: `ID: ${reg.player_2_id}`
								} (Hạng: ${getLevelNameFromId(reg.player_2_level_id)})`}
							  </>
							)}

                                </span>
                                <div>
                                  <Badge
                                    bg={reg.approved ? "success" : "secondary"}
                                  >
                                    {reg.approved ? "Đã duyệt" : "Chưa duyệt"}
                                  </Badge>
                                  <Button
                                    variant={reg.approved ? "warning" : "info"}
                                    size="sm"
                                    className="mx-2"
                                    onClick={() =>
                                      handleToggleApproval(
                                        tourney.id,
                                        reg.player_1_id,
                                      )
                                    }
                                  >
                                    {reg.approved ? "Hủy duyệt" : "Duyệt"}
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() =>
                                      handleDeleteRegistration(
                                        tourney.id,
                                        reg.player_1_id,
                                      )
                                    }
                                  >
                                    Xóa
                                  </Button>
                                </div>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : (
                          <p>Không tìm thấy VĐV nào.</p>
                        )}
                        {totalPages > 1 && (
                          <Pagination className="mt-3 justify-content-center">
                            {[...Array(totalPages).keys()].map((number) => (
                              <Pagination.Item
                                key={number + 1}
                                active={number + 1 === currentPage}
                                onClick={() =>
                                  setCurrentPages({
                                    ...currentPages,
                                    [tourney.id]: number + 1,
                                  })
                                }
                              >
                                {number + 1}
                              </Pagination.Item>
                            ))}
                          </Pagination>
                        )}
                      </>
                    );
                  })()}
                </ListGroup.Item>

                {/* Schedule Display */}
                {schedules[tourney.id] && (
                  <ListGroup.Item>
                    <h5>Lịch thi đấu</h5>
                    <Table striped bordered hover responsive size="sm">
                      <thead>
                        <tr>
                          <th>Vòng</th>
                          <th>Đội 1</th>
                          <th>Đội 2</th>
                          <th>Kết quả</th>
                        </tr>
                      </thead>
                      <tbody>
                        {schedules[tourney.id].map(match => (
                          <tr key={match.id}>
                            <td>{match.round_name}</td>
                            <td>
                              {playerMap.get(match.player_1_id) || `ID: ${match.player_1_id}`}
                              {match.player_2_id !== 0 &&
                                ` & ${playerMap.get(match.player_2_id) || `ID: ${match.player_2_id}`}`
                              }
                            </td>
                            <td>
                              {playerMap.get(match.player_3_id) || `ID: ${match.player_3_id}`}
                              {match.player_4_id !== 0 &&
                                ` & ${playerMap.get(match.player_4_id) || `ID: ${match.player_4_id}`}`
                              }
                            </td>
                            <td>{match.result || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </ListGroup.Item>
                )}

                <ListGroup.Item className="text-end">
                  <Button
                    variant="secondary"
                    className="me-2"
                    onClick={() => handleFetchSchedule(tourney.id)}
                    disabled={loading || tourney.status !== 'draw_generated'}
                  >
                    {schedules[tourney.id] ? "Ẩn Lịch" : "Xem Lịch"}
                  </Button>
                  <Button
                    variant="success"
                    onClick={() => handleGenerateDraw(tourney.id)}
                    disabled={
                      loading ||
                      tourney.registrations.length < 3 ||
                      tourney.status !== "registration_open"
                    }
                  >
                    {loading && <Spinner size="sm" />} Chia bảng & Tạo lịch
                  </Button>
                  <Button
                    variant="info"
                    className="ms-2"
                    onClick={() => handleShowEditModal(tourney.id)}
                    disabled={loading}
                  >
                    Chỉnh sửa
                  </Button>
                  <Button
                    variant="danger"
                    className="ms-2"
                    onClick={() => handleDeleteTournament(tourney.id)}
                    disabled={loading}
                  >
                    Xóa Giải đấu
                  </Button>
                </ListGroup.Item>
              </ListGroup>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>

      {/* Edit Tournament Modal */}
      {editingTournament && (
        <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Chỉnh sửa Giải đấu: {editingTournament.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tên Giải đấu</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingTournament.name || ''}
                      onChange={(e) => {
                        const newName = e.target.value;
                        const updatedTournament = { ...editingTournament, name: newName };
                        setEditingTournament(updatedTournament);
                        setEditingTournamentContent(JSON.stringify(updatedTournament, null, 2));
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                   <Form.Group className="mb-3">
                    <Form.Label>Ngày bắt đầu</Form.Label>
                    <Form.Control
                      type="date"
                      value={editingTournament.start_date || ''}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        const updatedTournament = { ...editingTournament, start_date: newDate };
                        setEditingTournament(updatedTournament);
                        setEditingTournamentContent(JSON.stringify(updatedTournament, null, 2));
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

               <Form.Group className="mb-3">
                <Form.Label>Địa điểm</Form.Label>
                <Form.Control
                  type="text"
                  value={editingTournament.location || ''}
                  placeholder="Ví dụ: Nhà thi đấu ABC"
                  onChange={(e) => {
                    const newLocation = e.target.value;
                    const updatedTournament = { ...editingTournament, location: newLocation };
                    setEditingTournament(updatedTournament);
                    setEditingTournamentContent(JSON.stringify(updatedTournament, null, 2));
                  }}
                />
              </Form.Group>

              <hr />

              <Form.Group className="mb-3">
                <Form.Label>Nội dung JSON đầy đủ</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={15}
                  value={editingTournamentContent}
                  onChange={(e) => setEditingTournamentContent(e.target.value)}
                  onBlur={() => {
                     try {
                        const parsed = JSON.parse(editingTournamentContent);
                        setEditingTournament(parsed); // Sync the simple fields if JSON is valid
                      } catch (e) {
                        // Do nothing if JSON is invalid, keep the original simple field values
                      }
                  }}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Hủy
            </Button>
            <Button variant="primary" onClick={handleSaveEdit} disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Lưu thay đổi"}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

    </Container>
  );
};

export default TournamentManagement;
